import { useEffect, useState } from "react";
import api, { formatApiError, setActiveRegistration } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const FIELDS = ["title", "description", "priority", "assignee", "due_date"];
const L = ({ t, children }) => <div><div className="text-xs font-medium mb-1">{t}</div>{children}</div>;

export default function ClaroTicketQuickEdit({ ticketId, onClose, onSaved }) {
  const [t, setT] = useState(null);
  const [f, setF] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!ticketId) return;
    api.get(`/console/claro-tickets/${ticketId}/detail`).then(({ data }) => {
      setT(data); setF(Object.fromEntries(FIELDS.map((k) => [k, data[k] || ""]))); setNote("");
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [ticketId]);

  const save = async () => {
    const changed = Object.fromEntries(FIELDS.filter((k) => (f[k] || "") !== (t[k] || "")).map((k) => [k, f[k]]));
    if (!Object.keys(changed).length && !note.trim()) { onClose(); return; }
    setBusy(true);
    try { await api.put(`/console/claro-tickets/${ticketId}`, { ...changed, note }); toast.success("Ticket updated"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const openFull = () => {
    setActiveRegistration("claro-internal");
    window.location.assign(`/claroflow?category=${t?.category || "security"}&ticket=${ticketId}`);
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={!!ticketId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="claro-ticket-quick-edit">
        <DialogHeader><DialogTitle>Edit Claro ticket</DialogTitle></DialogHeader>
        {t && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Status: <b>{t.status}</b> · Source: {t.source || "manual"} · Created {t.created_at?.slice(0, 10)}</div>
            <L t="Title"><Input value={f.title} onChange={set("title")} className="rounded-none" data-testid="cte-title" /></L>
            <L t="Description"><Textarea rows={5} value={f.description} onChange={set("description")} className="rounded-none" data-testid="cte-description" /></L>
            <div className="grid grid-cols-3 gap-2">
              <L t="Priority">
                <Select value={f.priority || "normal"} onValueChange={(v) => setF((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="cte-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "normal", "high"].map((p) => <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </L>
              <L t="Assignee"><Input value={f.assignee} onChange={set("assignee")} className="rounded-none" data-testid="cte-assignee" /></L>
              <L t="Due date"><Input type="date" value={f.due_date} onChange={set("due_date")} className="rounded-none" data-testid="cte-due" /></L>
            </div>
            <L t="Note for ticket history (optional)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="cte-note" /></L>
            <div className="flex flex-wrap gap-2 justify-between">
              <Button variant="outline" className="rounded-none" onClick={openFull} data-testid="cte-open-full">Open full ticket (status, evidence)</Button>
              <Button className="rounded-none" onClick={save} disabled={busy} data-testid="cte-save">{busy ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
